// LUMINA · pages/vajra/DharmaHub.jsx
// Phase 40+76B: 法讯 Tab — 活动列表 + 日历视图 + 定期共修（RecurringSchedule）

import React from 'react';
import { API, GDH, TXT, TXT2 } from './constants';
import SanghaBoard from './components/SanghaBoard';
import PublicEventCalendar from './components/PublicEventCalendar';

const RECURRENCE_LABELS = {
  weekly_sat: "每周六",
  weekly_sun: "每周日",
  weekly_fri: "每周五",
  weekly:     "每周",
  biweekly:   "每两周",
  monthly:    "每月",
  custom:     "不定期",
};

const TZ_LABELS = {
  "America/Toronto":   "ET",
  "America/Vancouver": "PT",
  "America/New_York":  "ET",
  "Asia/Shanghai":     "北京",
  "Asia/Hong_Kong":    "香港",
  "Europe/London":     "伦敦",
  "UTC":               "UTC",
};

// ── Calendar View (fetches all public events + schedules) ──
function CalendarView({ lang }) {
  const [calEvents, setCalEvents] = React.useState([]);
  const [calSchedules, setCalSchedules] = React.useState([]);
  const [calMonth, setCalMonth] = React.useState(new Date());

  React.useEffect(() => {
    fetch(`${API}/vajra/events/public`)
      .then(r => r.ok ? r.json() : []).then(d => setCalEvents(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/vajra/schedules`)
      .then(r => r.ok ? r.json() : []).then(d => setCalSchedules(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // Merge schedules into calendar items
  const scheduleItems = calSchedules
    .filter(s => s.is_active !== false && s.next_date)
    .map(s => ({
      id: `sched-${s.id}`,
      title_zh: s.title_zh,
      title_en: s.title_en,
      teacher_zh: s.teacher_zh,
      start_date: s.next_date,
      start_time: s.time_start,
      event_type: 'weekly_practice',
      _isSchedule: true,
    }));

  const allCalItems = [...calEvents, ...scheduleItems];

  // Auto-navigate to nearest upcoming event
  React.useEffect(() => {
    if (allCalItems.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    const dates = allCalItems.map(e => e.start_date).filter(Boolean).sort();
    const future = dates.filter(d => d >= today);
    const target = future[0] || dates[dates.length - 1];
    if (target) setCalMonth(new Date(target));
  }, [calEvents, calSchedules]);

  return (
    <div style={{ padding: "16px 0" }}>
      <div style={{ fontSize: 14, color: "#aaa", marginBottom: 16, lineHeight: 1.8 }}>
        {lang === "en" ? "Events · Practice sessions · Teachings" : "法会行程 · 共修通知 · 仁波切开示"}
      </div>
      <PublicEventCalendar
        events={allCalItems}
        calMonth={calMonth}
        setCalMonth={setCalMonth}
        lang={lang}
      />
    </div>
  );
}

export default function DharmaHub({ token, member, lang, T }) {
  const t = T || {};
  const [view, setView] = React.useState("list");
  const [schedules, setSchedules] = React.useState([]);

  React.useEffect(() => {
    if (view === "recurring") {
      fetch(`${API}/vajra/schedules`)
        .then(r => r.ok ? r.json() : [])
        .then(d => setSchedules(Array.isArray(d) ? d : []))
        .catch(() => {});
    }
  }, [view]);

  const btnStyle = (k) => ({
    padding: "6px 20px", borderRadius: 20, fontSize: 12, cursor: "pointer",
    background: view === k ? GDH : "transparent",
    border: `1px solid ${view === k ? GDH : "rgba(212,175,55,0.3)"}`,
    color: view === k ? "#fff" : GDH,
    marginRight: 8,
  });

  return (
    <div>
      <div style={{ padding: "20px 0 8px" }}>
        <button style={btnStyle("list")}      onClick={() => setView("list")}>{t.dharmaListView || "活动列表"}</button>
        <button style={btnStyle("calendar")}  onClick={() => setView("calendar")}>{t.dharmaCalendarView || "日历视图"}</button>
        <button style={btnStyle("recurring")} onClick={() => setView("recurring")}>{t.dharmaRecurring || "定期共修"}</button>
      </div>

      {view === "list" && <SanghaBoard token={token}/>}

      {view === "calendar" && (
        <CalendarView lang={lang} />
      )}

      {view === "recurring" && (
        <div style={{ padding: "16px 0" }}>
          {schedules.length === 0 ? (
            <div style={{ color: TXT2, fontSize: 13, textAlign: "center", padding: "40px 0" }}>
              {t.dharmaNoUpcoming || "暂无定期共修安排"}
            </div>
          ) : (
            schedules.map(sc => (
              <div key={sc.id} style={{
                background: "#fff", borderRadius: 12, padding: "18px 20px", marginBottom: 14,
                border: "1px solid rgba(212,175,55,0.15)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}>
                {/* 标题行 */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: 15, color: TXT, fontWeight: 600 }}>{lang === 'en' && sc.title_en ? sc.title_en : sc.title_zh}</div>
                    {sc.teacher_zh && (
                      <div style={{ fontSize: 12, color: TXT2, marginTop: 2 }}>🪷 {sc.teacher_zh}</div>
                    )}
                  </div>
                  <span style={{
                    fontSize: 10, color: GDH, background: "rgba(212,175,55,0.08)",
                    border: "1px solid rgba(212,175,55,0.15)", borderRadius: 10, padding: "2px 10px",
                    flexShrink: 0, marginLeft: 8,
                  }}>
                    {RECURRENCE_LABELS[sc.recurrence] || sc.recurrence}
                  </span>
                </div>

                {/* 时间信息 */}
                <div style={{ marginTop: 10, fontSize: 12, color: TXT2, lineHeight: 1.8 }}>
                  {sc.next_date && <div>📅 下次：{sc.next_date}</div>}
                  {sc.time_start && (
                    <div>
                      ⏰ {sc.time_start}{sc.time_end ? `–${sc.time_end}` : ""}{" "}
                      {TZ_LABELS[sc.timezone] || sc.timezone}
                    </div>
                  )}
                  {sc.location && <div>📍 {sc.location}</div>}
                  {sc.online_link && (
                    <div>
                      💻 <a href={sc.online_link} target="_blank" rel="noopener noreferrer"
                        style={{ color: DG }}>线上链接</a>
                    </div>
                  )}
                </div>

                {sc.description && (
                  <div style={{ marginTop: 8, fontSize: 12, color: TXT2, lineHeight: 1.7 }}>
                    {sc.description}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
